import dayjs from "dayjs";
import { METRICS_PERIODS, nonUniqueTypes } from "./constants/MetricsConstants";
import { toLocaleNumberString } from "./stringHelpers";
import { isAccept2Enabled } from "./prompts/promptHelpers";
import { dateOrder } from "./DateDisplayUtils";

export const tally = (data, metricsKey, statType) => {
  if (!data?.[statType]?.[metricsKey]) return 0;

  const chart = data[statType][metricsKey];

  // serialize=list collapses each period to { uniques, total } — prefer total
  // for non-unique metrics (the sum of the former day series)
  if (nonUniqueTypes.includes(statType) && chart.total != null) return chart.total;

  if (!chart.data) return chart.uniques || 0;

  // don't have uniques for impressions
  if (nonUniqueTypes.includes(statType) || chart.uniques == null)
    return chart.data.reduce((acc, { count }) => acc + count, 0);

  return chart.uniques || 0;
};

export const percentage = (numerator, denominator, format = false) => {
  if (denominator === 0)
    return format === "new" ? { value: 0, label: `${toLocaleNumberString(0, 2)}%` } : 0;

  const decimal = numerator / (denominator || 1);
  const wholeNumber = decimal * 100;

  if (format === "new")
    return {
      value: +wholeNumber.toFixed(2),
      label: `${toLocaleNumberString(+wholeNumber, 2)}%`,
    };

  if (format) return wholeNumber ? `${toLocaleNumberString(+wholeNumber.toFixed(2))}%` : 0;
  return parseFloat(Math.round(100 * wholeNumber) / 100) || 0;
};

export const statsAggregator = (items, metricsKey) =>
  items.reduce((aggData, path) => {
    if (!path) return aggData;

    Object.entries(path.data).forEach(([dataType, periodData]) => {
      periodData = periodData || {};

      const chart = periodData[metricsKey];
      if (!chart) return;

      if (!aggData[dataType]) aggData[dataType] = {};

      // serialize=list collapses each period to { uniques, total }: sum both
      // across paths, mirroring the day-array merge below.
      if (chart.total != null && !Array.isArray(chart.data)) {
        const existing = aggData[dataType][metricsKey];
        if (!existing) {
          aggData[dataType][metricsKey] = {
            tz_offset: periodData.tz_offset,
            uniques: chart.uniques,
            total: chart.total,
          };
        } else {
          existing.total = (existing.total || 0) + chart.total;
          existing.uniques = (existing.uniques || 0) + (chart.uniques || 0);
        }
        return;
      }

      const pathData = Array.isArray(chart.data) && chart.data.length;
      if (!pathData) return;

      if (!aggData[dataType][metricsKey]) {
        aggData[dataType][metricsKey] = {
          tz_offset: periodData.tz_offset,
          ...chart,
        };
      } else if (aggData[dataType][metricsKey].data) {
        aggData[dataType][metricsKey].data = aggData[dataType][metricsKey].data.map(
          (dateWithCount, idx) => {
            let count = dateWithCount && dateWithCount.count;
            if (count !== null) {
              const period = periodData[metricsKey].data[idx];
              if (period && period.count) count += period.count;
            }
            return { ...dateWithCount, count };
          },
        );
        const uniques = aggData[dataType][metricsKey].uniques + periodData[metricsKey].uniques;
        aggData[dataType][metricsKey] = {
          ...aggData[dataType][metricsKey],
          uniques,
        };
      }
    });

    return aggData;
  }, {});

export const getTotals = (metrics, type, period) => {
  const typeData = metrics?.[type]?.[period];
  if (!typeData) return 0;
  if (type !== "impressions") return typeData.uniques;
  // serialize=list provides the impression sum as `total`
  if (typeData.total != null) return typeData.total;
  // don't have uniques for impressions
  return (typeData.data || [])
    .filter(item => item.count !== null)
    .reduce((acc, { count }) => acc + count, 0);
};

export const getSurveyInputs = path => {
  const totalOptionsCount = path?.actions?.rf_retention_survey_options_total;
  const res = { labels: [], values: [] };
  if (!totalOptionsCount) return res;

  const base = "rf_retention_survey_option";

  for (let i = 1; i <= totalOptionsCount; i++) {
    const label = `${base}_${i}_label`;
    const value = `${base}_${i}_value`;
    if (path.actions[label] && path.actions[value]) {
      res.labels.push(path.actions[label]);
      res.values.push(path.actions[value]);
    }
  }
  return res;
};

/**
 * Builds the survey series data from all server-returned metrics, not only the
 * ones matching the prompt's current survey settings.
 *
 * One entry is produced per current survey option value, duplicate values kept
 * as before, followed by one entry per server key absent from the current
 * settings, so results from other experiment variants are kept. Known keys are
 * labeled with their survey input label, unknown ones with the raw key.
 *
 * @param {Object} [surveyInputs] - current survey settings from `getSurveyInputs`
 * @param {string[]} [surveyInputs.labels] - display labels per option value
 * @param {string[]} [surveyInputs.values] - current survey option values
 * @param {Object<string, number>} [surveyMetrics] - server counts keyed by option value
 * @returns {{label: string, key: string, value: number}[]} series entries in
 *   settings order first, then server-only keys in server order
 *
 * @example
 * getSurveySeriesData(
 *   { labels: ["Yes", "No"], values: ["yes", "no"] },
 *   { yes: 10, no: 5, "other variant": 3 },
 * );
 * // [{ label: "Yes", key: "yes", value: 10 }, { label: "No", key: "no", value: 5 },
 * //  { label: "other variant", key: "other variant", value: 3 }]
 */
export const getSurveySeriesData = (surveyInputs, surveyMetrics) => {
  const labels = surveyInputs?.labels || [];
  const values = surveyInputs?.values || [];
  const metrics = surveyMetrics || {};

  const inputs = values.map((key, i) => ({ label: labels[i], key, value: metrics[key] || 0 }));
  const extra = Object.keys(metrics)
    .filter(key => !values.includes(key))
    .map(key => ({ label: key, key, value: metrics[key] || 0 }));

  return [...inputs, ...extra];
};

export const getPeriod = (item, statType, period) => {
  const tempStat = item.data[statType];
  if (tempStat && tempStat[period]) {
    const chart = tempStat[period];
    if (chart) {
      return chart.data.map(el => el.count);
    }
  }
  return [];
};

export const getHigh = (item, period) => {
  const chart = item.data.users[period];
  if (chart) {
    return Math.max(...chart.data.map(el => el.count));
  }
  return 0;
};

export const getPeriodHeader = (item, statType, currChartSrc) => {
  let chart;
  const tempStat = item.data[statType];
  if (tempStat) {
    chart = tempStat[currChartSrc];
  } else {
    const tempStatType = Object.keys(item.data)[0];
    if (!tempStatType) return [];
    chart = item.data[tempStatType][currChartSrc];
  }
  return chart.data.map(dataItem => {
    const date = dayjs(`${dataItem.date}`, ["YYYYMMDD", "YYYYMMDDHHmm"]).utc(true);

    if ([METRICS_PERIODS.today.value, METRICS_PERIODS.yesterday.value].includes(currChartSrc))
      return date.format("hA");
    return date.format(`${dateOrder("-")}-YYYY`);
  });
};

export const DISPLAYED_METRICS = {
  goals: "Accept",
  goals2: "Secondary Accept",
  dismissals: "Dismiss",
  declines: "Decline",
  timeouts: "Timeout",
  sends_uniq: "Endpoints",
  sends: "Attempted Deliveries",
  deliveries: "Successful Deliveries",
};

export const CUSTOM_DISPLAYED_METRICS = {
  custom_goals_accepted: "after Accept",
  custom_goals_accepted2: "after Secondary Accept",
  custom_goals_declined: "after Decline",
  custom_goals_dismissed: "after Dismiss/Timeout",
};

export const getDisplayMetricsText = (model, key, defaultValue = null) => {
  if (
    model?.actions?.rf_settings_tile_interaction === "container" &&
    key === "rf_retention_button1_text"
  )
    return defaultValue;
  if (!model?.filter?.is_translated) return model?.actions?.[key];
  return (
    Object.values(model?.parsed_translations || {}).find(el => el?.default === "TRUE")?.[key] ||
    defaultValue
  );
};

export const getDisplayMetrics = prompt => ({
  goals: getDisplayMetricsText(prompt, "rf_retention_button1_text", DISPLAYED_METRICS.goals),
  ...(isAccept2Enabled(prompt) && {
    goals2: getDisplayMetricsText(prompt, "rf_retention_button2_text", DISPLAYED_METRICS.goals2),
  }),
  declines: getDisplayMetricsText(prompt, "rf_retention_button3_text", DISPLAYED_METRICS.declines),
  dismissals: DISPLAYED_METRICS.dismissals,
  timeouts: DISPLAYED_METRICS.timeouts,
  sends_uniq: DISPLAYED_METRICS["sends_uniq"],
  sends: DISPLAYED_METRICS["sends"],
  deliveries: DISPLAYED_METRICS["deliveries"],
});

export const getDisplayCustomMetrics = (prompt, displayMetrics = getDisplayMetrics(prompt)) => {
  return {
    custom_goals_accepted: `after ${displayMetrics.goals}`,
    custom_goals_accepted2: `after ${displayMetrics.goals2}`,
    custom_goals_declined: `after ${displayMetrics.declines}`,
    custom_goals_dismissed: CUSTOM_DISPLAYED_METRICS.custom_goals_dismissed,
  };
};

export const newChartHasData = data => {
  if (!data || !data?.length || !Array.isArray(data)) return false;

  return !!data.find(
    mt =>
      !!mt &&
      !!Object.values(mt.data || {}).find(el => !!(typeof el === "object" ? el?.label : el)),
  );
};
